import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { getAuthToken, setUser } from '../features/userSlice'
import { type BaseQueryFnType, type IAuthToken, type IMultisiteGetQuery, type IUserProfile, type IIHFCredentials } from './types'
import { HEADERS_TITLE } from './constants'
import { AUTH_END_POINT } from './endpoints'

const BASE_URL = process.env.REACT_APP_API_URL as string

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      headers.set('Accept', 'application/json')
      return headers
    }
  }) as BaseQueryFnType,
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getToken: builder.mutation<void, void>({
      query () {
        return {
          url: AUTH_END_POINT.CSRF,
          method: 'GET',
          credentials: 'include'
        }
      }
    }),
    login: builder.mutation({
      query: (credentials) => {
        const { email, password, token } = credentials
        return {
          url: AUTH_END_POINT.LOGIN,
          method: 'POST',
          body: { email, password },
          credentials: 'include',
          headers: {
            [HEADERS_TITLE.XSRF]: token
          }
        }
      }
    }),
    multisiteSPA: builder.mutation({
      query: (credentials) => {
        const { bearerToken } = credentials
        return {
          url: AUTH_END_POINT.MULTISITE_SPA,
          method: 'GET',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${String(bearerToken)}`

          }
        }
      }
    }),
    multisiteProfile: builder.mutation<IUserProfile, IMultisiteGetQuery>({
      query: (credentials) => {
        const { bearerToken } = credentials
        return {
          url: AUTH_END_POINT.MULTISITE_PROFILE,
          method: 'GET',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${String(bearerToken)}`
          }
        }
      },
      transformResponse: (res: IUserProfile) => {
        return res
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setUser(data))
        } catch (error) {
          console.error(error)
        }
      }
    }),
    authToken: builder.mutation({
      query: () => {
        return {
          url: AUTH_END_POINT.AUTH_TOKEN,
          method: 'GET',
          credentials: 'include'
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      transformResponse: (res: IAuthToken) => {
        return res
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(getAuthToken(data))
        } catch (error) {
          // console.error(error)
        }
      }
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    logout: builder.mutation<void, void>({
      query: () => {
        return {
          url: AUTH_END_POINT.LOGOUT,
          credentials: 'include'
        }
      }
    }),
    multisiteIHFCredentials: builder.mutation<IIHFCredentials, IMultisiteGetQuery>({
      query: (credentials) => {
        const { bearerToken } = credentials
        return {
          url: AUTH_END_POINT.MULTISITE_IHF_LOGIN,
          method: 'GET',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${String(bearerToken)}`
          }
        }
      },
      transformResponse: (res: IIHFCredentials) => {
        return res
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setUser(data))
        } catch (error) {
          console.error(error)
        }
      }
    }),
    getStudioToken: builder.mutation<string, string>({
      query: (xsrf) => {
        return {
          url: AUTH_END_POINT.STUDIO_TOKEN,
          credentials: 'include',
          method: 'GET',
          headers: {
            [HEADERS_TITLE.XSRF]: xsrf
          }
        }
      },
      transformResponse: (res: string) => {
        return res
      }
    }),
    multisiteStudioToken: builder.mutation<string, IMultisiteGetQuery>({
      query: (credentials) => {
        const { bearerToken } = credentials
        return {
          url: AUTH_END_POINT.MULTISITE_STUDIO_TOKEN,
          method: 'GET',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${String(bearerToken)}`
          }
        }
      },
      transformResponse: (res: string) => {
        return res
      }
    })
  })
})

export const {
  useGetTokenMutation,
  useLoginMutation,
  useMultisiteSPAMutation,
  useMultisiteProfileMutation,
  useLogoutMutation,
  useAuthTokenMutation,
  useMultisiteIHFCredentialsMutation,
  useGetStudioTokenMutation,
  useMultisiteStudioTokenMutation
} = authApi
